<template>
  <div :id="id" :class="{row: horizontal}">
    <label :id="idLabel" v-if="inputLabel && !hiddenLabel" :class="customLabelClass">
      {{ inputLabel }}
      <span v-if="isMandatory" class="color-red">* </span>
    </label>
    <div v-if="inputType === 'number'" :class="customInputClass">
      <input class="form-control inputAccordionForm" type="number"
        :id="idData"
        :class="errorIndicatorHighlight"
        :style="{height: customHeight !== 'none' ? customHeight + 'px' : 'calc(1.5em + 0.75rem + 2px)' }"
        :disabled="disabled"
        :placeholder="placeholderValue" v-model="inputValue"
        :min="minValue" :max="maxValue"
        @blur="verifyIncorrectValue()"
        @input="inputTypingEvent"
        step="any">
      </input>
    </div>
    <div v-else-if="inputType === 'text' || inputType === 'password'" :class="customInputClass">
      <input class="form-control inputAccordionForm" :type="inputType"
        :id="idData"
        :disabled="disabled"
        :class="errorIndicatorHighlight"
        :style="{height: customHeight !== 'none' ? customHeight + 'px' : 'calc(1.5em + 0.75rem + 2px)' }"
        :placeholder="placeholderValue" v-model="inputValue"
        :maxlength="inputType === 'text' ? maxLength:''"
        @blur="verifyIncorrectValue()"
        @input="inputTypingEvent">
      </input>
      <slot></slot>
    </div>
    <div class="formDatePicker" v-else-if="inputType === 'date'" :class="customInputClass">
      <date-picker v-model="inputValue" :format="customFormatter"
        :id="idData"
        :disabled="disabled"
        :placeholder="placeholderValue" :language="languageValue"
        :style="{height: customHeight !== 'none' ? customHeight + 'px' : 'calc(1.5em + 0.75rem + 2px)' }"
        :class="errorIndicatorHighlight" input-class="form-control inputAccordionForm"
        :disabledDates="disabledDates">
      </date-picker>
    </div>
    <div :id="idData" v-else :class="customInputClass"> <!-- Somes fields like drop-down lists only require a slot -->
      <slot></slot>
    </div>
    <div :id="idError" v-if="regexpErrorIndicator" style="color:red;">
      {{ $t('errors.is_incorrect', { incorrectValue: inputLabel }) }}
    </div>
    <div :id="idError" v-else-if="componentMandatoryErrorIndicator" style="color:red;">
      {{ $t('errors.is_mandatory', { mandatoryValue: inputLabel }) }}
    </div>
    <div :id="idError" v-else-if="intervalErrorIndicator" style="color:red;">
      {{ $t('errors.not_in_interval', { outOfRangeValue: inputLabel }) }}
    </div>
    <div :id="idError" v-else-if="minErrorIndicator" style="color:red;">
      {{ $t('errors.must_be_superior_to', { fieldName: inputLabel, minValue: minValue }) }}
    </div>
    <div :id="idError" v-else-if="maxErrorIndicator" style="color:red;">
      {{ $t('errors.must_be_inferior_than', { fieldName: inputLabel, maxValue: maxValue }) }}
    </div>
    <div :id="idError" v-else-if="specificErrorIndicator" :class="customErrorClass" style="color:red;">
      {{ specificMessage }}
    </div>
    <div :id="idError" v-else-if="warningIndicator" :class="customErrorClass" style="color:orange;">
      {{ specificMessage }}
    </div>
    <slot name="additionalMessage"></slot>
  </div>
</template>
<script>
/* @author TLV 25/06/2020
* @brief Component utilisé pour les champs à remplir pour envoyer un formulaire
*/

import Datepicker from 'vuejs-datepicker'
import * as lang from 'vuejs-datepicker/src/locale'
import moment from 'moment'

export default {
  name: 'formWriteGroupLabelInput',
  props: {
    id: {
      type: String,
      default: undefined
    },
    colInputSize: {
      type: Number,
      default: 7
    },
    colLabelSize: {
      type: Number,
      default: 5
    },
    hiddenLabel: {
      type: Boolean,
      default: false
    },
    horizontal: {
      type: Boolean,
      default: false
    },
    placeholderValue: {
      type: String
    },
    isMandatory: {
      type: Boolean,
      default: false
    },
    inputLabel: {
      type: String,
      default: ''
    },
    inputType: {
      type: String,
      default: 'text'
    },
    inputValues: {
      type: Array,
      default: null
    },
    value: {
      type: String,
      default: ''
    },
    minValue: {
      type: Number,
      default: 0
    },
    maxValue: {
      type: Number,
      default: Number.MAX_VALUE // 1.7976931348623157e+308
    },
    disabled: {
      type: Boolean,
      default: false
    },
    customHeight: {
      type: String,
      default: 'none'
    },
    disablePastDates: {
      type: Boolean,
      default: false
    },
    disableFromDate: {
      type: String,
      default: ''
    },
    mandatoryErrorIndicator: {
      type: Boolean,
      default: false
    },
    regexpErrorIndicator: {
      type: Boolean,
      default: false
    },
    intervalErrorIndicator: {
      type: Boolean,
      default: false
    },
    warningIndicator: {
      type: Boolean,
      default: false
    },
    specificMessage: {
      type: String,
      default: ''
    },
    specificErrorIndicator: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number,
      default: 70
    },
    typingDone: {
      type: Function,
      default: function () {}
    },
    typingInterval: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      componentMandatoryErrorIndicator: this.mandatoryErrorIndicator,
      customErrorClass: '',
      customInputClass: '',
      customLabelClass: '',
      maxErrorIndicator: false,
      minErrorIndicator: false
    }
  },
  components: {
    'date-picker': Datepicker
  },
  computed: {
    // pseudo v-model avec le component parent
    idLabel () {
      return this.id ? this.id + '-label' : undefined
    },
    idData () {
      return this.id ? this.id + '-data' : undefined
    },
    idError () {
      return this.id ? this.id + '-error' : undefined
    },
    inputValue: {
      get () {
        return (this.inputType === 'number' && this.value === '.') ? 0 : this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    languageValue () {
      return lang[this.$i18n.locale]
    },
    errorIndicatorHighlight () {
      if (this.componentMandatoryErrorIndicator || this.regexpErrorIndicator || this.intervalErrorIndicator || this.specificErrorIndicator) {
        return 'errorBorder'
      }
      if (this.warningIndicator) {
        return 'warningBorder'
      }
      return ''
    },
    disabledDates () {
      var dates = {}
      if (this.disablePastDates) {
        dates['to'] = new Date(Date.now() - 86400000)
      }
      if (this.disableFromDate) {
        dates['from'] = new Date(this.disableFromDate)
      }
      return dates
    }
  },
  created () {
    if (this.horizontal) {
      this.customInputClass = 'col-' + this.colInputSize + ' p-0'
      this.customLabelClass = 'col-' + this.colLabelSize + ' p-0'
      this.customErrorClass = 'offset-' + this.colLabelSize + ' col-' + this.colInputSize + ' p-0'
    }
  },
  methods: {
    customFormatter (date) {
      return moment(date).format(this.$t('common.datepicker_format_date'))
    },
    inputTypingEvent () {
      clearTimeout(this.typingTimer)
      this.typingTimer = setTimeout(this.typingDone, this.typingInterval)
    },
    verifyIncorrectValue () {
      this.componentMandatoryErrorIndicator = this.isMandatory && this.inputValue === ''
      this.minErrorIndicator = this.inputType === 'number' && this.inputValue < this.minValue
      this.maxErrorIndicator = this.inputType === 'number' && this.inputValue > this.maxValue
      this.$emit('blur')
    }
  }
}
</script>



// WEBPACK FOOTER //
// formWriteGroupLabelInput.vue?f4ac0fee